import { styled, TextField } from '@mui/material';
import Grid from 'components/core/Grid';
import { AlluraFormikErrorMessage } from 'components/formik/core/AlluraErrorMessage/AlluraFormikErrorMessage';
import { VerifyCodeFormikValues } from 'components/views/verification/components/VerifyCodeForm';
import { useField, useFormikContext } from 'formik';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import * as Sentry from '@sentry/nextjs';

const CODE_LENGTH = 6;
const CODE_ARRAY = Array.from(Array(CODE_LENGTH));

const CodeWrapper = styled(Grid)`
  display: flex;
  gap: ${props => props.theme.spacing(1)};

  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  /* Firefox */
  input[type='number'] {
    -moz-appearance: textfield;
  }

  input {
    text-align: center;
  }
`;

function isDeleteKeypress(e: any) {
  const key = e.key;
  if (key === 'Backspace' || key === 'Delete') {
    return true;
  }
  return false;
}

export const VerifyCodeInput = () => {
  const [, , helpers] = useField('code');
  const itemsRef = useRef<any[]>([]);
  const [currIndex, setCurrIndex] = useState(0);
  const { values, setFieldValue, submitForm } = useFormikContext<VerifyCodeFormikValues>();

  const onKeyDown = (e: any, index: number) => {
    const val = e.key;

    e.key === 'e' && e.preventDefault();

    const isDelete = isDeleteKeypress(e);
    const isNumber = /^[0-9]$/i.test(e.key);

    if (isDelete) {
      const currValue = values.code.split('');
      currValue.pop();

      setFieldValue('code', currValue.join(''));

      if (itemsRef.current && index > 0) {
        flushSync(() => {
          setCurrIndex(curr => curr - 1);
        });
        e.target.blur();
        itemsRef.current[index - 1].focus();
      }
    } else if (!isNumber || e.key === 'Shift' || e.key === 'e') {
      return;
    } else {
      helpers.setTouched(true);
      const currValue = values.code.split('');
      if (index > currValue.length - 1) {
        currValue.push(val);
      } else {
        currValue[index] = val;
      }

      setFieldValue('code', currValue.join(''));
      if (currValue.length === CODE_LENGTH) {
        submitForm();
      }

      if (itemsRef.current && index < CODE_LENGTH - 1) {
        flushSync(() => {
          setCurrIndex(curr => curr + 1);
        });
        e.target.blur();
        itemsRef.current[index + 1].focus();
      }
    }
  };

  const onPaste = async () => {
    try {
      if (navigator?.clipboard?.readText) {
        const text = await navigator.clipboard.readText();

        if (text && !isNaN(Number(text))) {
          const maxSubstringText = text.substring(0, 6);

          flushSync(() => {
            setCurrIndex(maxSubstringText.length - 1);
            setFieldValue('code', text.substring(0, 6));
          });
          itemsRef.current[maxSubstringText.length - 1].focus();

          if (maxSubstringText.length === CODE_LENGTH) {
            submitForm();
          }
        }
      } else {
        alert('Could not paste code, please enter the code manually');
      }
    } catch (e) {
      Sentry.captureException(e);
      alert('Could not paste code, please enter the code');
    }
  };

  return (
    <>
      <CodeWrapper item xs={12}>
        <>
          {CODE_ARRAY.map((_, index) => {
            const value = index > values.code.length - 1 ? '' : values.code[index];

            return (
              <TextField
                key={index}
                autoFocus={currIndex === index}
                inputProps={{}}
                inputRef={(el: any) => (itemsRef.current[index] = el)}
                type="number"
                value={value}
                onKeyDown={e => onKeyDown(e, index)}
                onPaste={onPaste}
                disabled={currIndex !== index}
              />
            );
          })}
        </>
      </CodeWrapper>
      <AlluraFormikErrorMessage fieldName="code" />
    </>
  );
};
